import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  message,
  Input,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { isMutable } from 'utils/estadosGlobales';
import { getModelPermission } from 'utils/permission/index';
import { capitalize } from 'utils/normalizers';
import { getUsuarios } from 'api/usuarios/usuarios';
import { CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import Placas from './Placas';
import ModalActionsPlacas from './ModalActionsPlacas';

const model = 'usuario';

// eslint-disable-next-line react/prop-types
const AsignacionDePlacas = ({ permission }) => {
  const [form] = Form.useForm();
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [permissionPlacas, setPermissionPlacas] = useState(null);
  const [mutable, setMutable] = useState(true);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [visibleModal, setVisibleModal] = useState(false);
  const [change, setChange] = useState(false);

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const permissionAsignacionPlacas = getModelPermission(model);
      if (!permissionAsignacionPlacas.hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setPermissionPlacas(permissionAsignacionPlacas);
        setHasAllViewPermission(permissionAsignacionPlacas.hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const _data = await getUsuarios();
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setMutable(true);
  };

  const onClickEdit = (response = null) => {
    const [key] = selectedRowKeys;
    const match = response || data.find((e) => e.id === key);
    form.setFieldsValue(match);
    setVisible(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Correo Electrónico',
      dataIndex: 'email',
      key: 'email',
      width: 300,
    },
    {
      titleText: 'Nombre Completo',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      width: 350,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estadosGlobales.find((t) => t.id === val)?.descripcion,
    },
  ];

  // const rules = {
  //   required: [{ required: true }],
  // };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickExpand: onClickEdit,
            }}
            customActions={[
              {
                text: 'Reasignar placas',
                onClick: () => {
                  setVisibleModal(true);
                  setChange(true);
                },
                icon: <SyncOutlined />,
              },
              {
                text: 'Dar de baja placas',
                onClick: () => {
                  setVisibleModal(true);
                  setChange(false);
                },
                icon: <CloseCircleOutlined />,
              },
            ]}
            allowExpand
            loading={loading}
            mobileColIndex={0}
            rowKey="id"
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
          />
        ) : (
          <Card
            className="form-container"
            title="Asignación de Placas"
            extra={(
              <FormSubmitControls
                onCancel={onCancel}
                mutable={mutable}
                loading={loading}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Col span={24}>
              <Form
                layout="vertical"
                form={form}
                scrollToFirstError
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="email"
                      label="Correo"
                      hasFeedback
                      normalize={capitalize}
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="first_name"
                      label="Nombre"
                      hasFeedback
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="last_name"
                      label="Apellido Paterno"
                      hasFeedback
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="second_last_name"
                      label="Apellido Materno"
                      hasFeedback
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  {!!permissionPlacas?.view && (
                    <Col span={24}>
                      <Placas
                        usuario={selectedRowKeys[0]}
                        permission={permissionPlacas}
                      />
                    </Col>
                  )}
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Row>
              </Form>
            </Col>
          </Card>
        )}
      </Spin>
      <ModalActionsPlacas
        visible={visibleModal}
        setVisible={setVisibleModal}
        usuario={data.find((e) => e.id === selectedRowKeys[0])}
        usuarios={data}
        change={change}
      />
    </Row>
  );
};

export default AsignacionDePlacas;
